import { Trans, useLingui } from "@lingui/react/macro";
import {
  Badge,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Spinner,
} from "@rakazo/ui-web";
import { useQuery } from "@tanstack/react-query";
import { errorText } from "../../lib/user-error";
import { drokRpc } from "../rpc";
import { useToolGroupInfo } from "./policy-fields";

interface VersionViewProps {
  templateId: string;
  /** The version number to show, or null when closed. */
  version: number | null;
  onClose: () => void;
}

/** One published version, read-only: instructions, routines, skills and the tool policy. */
export function VersionView({ templateId, version, onClose }: VersionViewProps) {
  const { t } = useLingui();
  const info = useToolGroupInfo();
  const query = useQuery({
    queryKey: ["drok", "admin.version", templateId, version],
    queryFn: () => drokRpc.templates.getVersion({ templateId, version: version ?? 1 }),
    enabled: version !== null,
  });
  const data = query.data;
  return (
    <Dialog open={version !== null} onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            <Trans>Version {version}</Trans>
          </DialogTitle>
          <DialogDescription>{data?.notes || t`No notes`}</DialogDescription>
        </DialogHeader>
        {query.isPending ? <Spinner aria-label={t`Loading…`} /> : null}
        {query.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {errorText(query.error)}
          </p>
        ) : null}
        {data ? (
          <div className="space-y-4 text-sm" data-testid="version-view">
            <section>
              <h3 className="font-medium">
                <Trans>Instructions</Trans>
              </h3>
              <pre className="mt-1 max-h-60 overflow-auto whitespace-pre-wrap rounded-lg bg-muted/50 p-3 text-xs">
                {data.snapshot.bot.instructions}
              </pre>
            </section>
            <section>
              <h3 className="font-medium">
                <Trans>Routines</Trans>
              </h3>
              {data.snapshot.routines.length === 0 ? (
                <p className="text-muted-foreground">
                  <Trans>None</Trans>
                </p>
              ) : (
                <ul className="list-disc ps-5">
                  {data.snapshot.routines.map((routine) => (
                    <li key={routine.itemKey}>
                      {routine.name}
                      <span className="text-muted-foreground">
                        {" "}
                        · {routine.crons.join(", ")} ({routine.timezone})
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section>
              <h3 className="font-medium">
                <Trans>Skills</Trans>
              </h3>
              {data.snapshot.skills.length === 0 ? (
                <p className="text-muted-foreground">
                  <Trans>None</Trans>
                </p>
              ) : (
                <ul className="list-disc ps-5">
                  {data.snapshot.skills.map((skill) => (
                    <li key={skill.itemKey}>
                      {skill.name}
                      <span className="text-muted-foreground"> · {skill.description}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section>
              <h3 className="font-medium">
                <Trans>Tools and apps</Trans>
              </h3>
              <div className="mt-1 flex flex-wrap gap-1">
                {data.policy.toolGroups.map((group) => (
                  <Badge key={group} variant="secondary">
                    {info[group].label}
                  </Badge>
                ))}
                {data.policy.toolkits.map((toolkit) => (
                  <Badge key={toolkit.slug} variant="outline">
                    {toolkit.name}
                  </Badge>
                ))}
                {data.policy.toolGroups.length + data.policy.toolkits.length === 0 ? (
                  <span className="text-muted-foreground">
                    <Trans>None</Trans>
                  </span>
                ) : null}
              </div>
            </section>
            {(data.snapshot.mcpServers ?? []).length > 0 ? (
              <section>
                <h3 className="font-medium">
                  <Trans>MCP connections</Trans>
                </h3>
                <ul className="mt-1 space-y-1">
                  {data.snapshot.mcpServers.map((server) => (
                    <li key={server.itemKey}>
                      {server.name}
                      <span className="text-muted-foreground">
                        {" · "}
                        {server.authMode === "shared" ? (
                          <Trans>Company credentials</Trans>
                        ) : (
                          <Trans>Employee credentials</Trans>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
